/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { BuilderUserContext } from '@gh/builder-shared';
import {
  ContactDeliveryMethod,
  SchedulingSuppliersRestService,
  SystemRoleBit,
  systemRolesBitSet,
  UserAutocompleteQuery,
  UserRestService,
} from '@gh/builder-shared-data';
import { DictionaryService, InitViewService } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { map$ } from '@gh/rx/operators';
import { map } from 'lodash';

import { Observable } from 'rxjs';

import {
  SchedulingSupervisorAutocomplete,
  SchedulingSupplierListDetailsUpdate,
  SchedulingSupplierListItem,
  supervisorSuppliersFromDto,
  supervisorSuppliersToDto,
} from './scheduling-supplier-list.model';

@Injectable()
export class SchedulingSupplierListBusinessService implements InitViewService {

  constructor(private restService: SchedulingSuppliersRestService,
              private userContext: BuilderUserContext,
              private userRestService: UserRestService,
              private dictionaryService: DictionaryService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    return {
      contactDeliveryMethod: this.dictionaryService.getByEnum(ContactDeliveryMethod),
      supplierList: this.isUserSupervisor()
        ? this.findAllSuppliers(this.userContext.data.id)
        : [],
    };
  }

  getAutocompleteSupervisorUser(query: FilteredDataQuery<UserAutocompleteQuery>): Observable<SchedulingSupervisorAutocomplete[]> {
    return this.userRestService.findAllAsDataSource({
      name: query.filter && query.filter.name || '',
      showInactive: false,
      autocomplete: true,
      systemRoles: systemRolesBitSet(SystemRoleBit.Supervisor),
    }).select(query).pipe(
      map$(getSlicedData),
      map$((supervisorList: SchedulingSupervisorAutocomplete[]) =>
        map(supervisorList, ({ userName, userId }) => ({ userName, userId }))));
  }

  updateSupervisorSuppliers({ supervisor: { userId }, suppliers }: SchedulingSupplierListDetailsUpdate): Observable<SchedulingSupplierListItem[]> {
    return this.restService.updateSupervisorSuppliers(userId, supervisorSuppliersToDto(suppliers)).pipe(
      map$(supervisorSuppliersFromDto));
  }

  findAllSuppliers(supervisorId: ID): Observable<SchedulingSupplierListItem[]> {
    return this.restService.findAllSuppliers(supervisorId).pipe(
      map$(supervisorSuppliersFromDto));
  }

  private isUserSupervisor(): boolean {
    return !this.isUserFranchisee() && this.userContext.hasSystemRole(SystemRoleBit.Supervisor);
  }

  private isUserFranchisee(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Franchisee)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasSystemRole(SystemRoleBit.Corporate);
  }
}
